import React, { useEffect, useMemo, useState } from 'react';

import { DateRange } from 'react-day-picker';

import { useAnalyticsAttributeNames } from 'api/useAnalyticsAttributeNames';
import { useAnalyticsAttributeValues } from 'api/useAnalyticsAttributeValues';
import { getFixedDays, getWholeRange } from 'views/Analytics/utils';

import DatePicker from 'components/DatePicker/DatePicker';
import { DatePickerMode } from 'components/DatePicker/DatePicker.types';
import FilterCommandCard from 'components/Filter/FilterCommandCard';

import {
  buildAttributeFilters,
  formatAttributeNameOptions,
  formatAttributeValueOptions
} from './utils';

interface AnalyticsBusinessTabHeaderProps {
  publisherId?: string;
  dateRange: DateRange;
  previousDateRange?: DateRange;
  onAttributeFiltersChange: (filters: Record<string, string[]>) => void;
  onDateRangeChange: (dateRange: DateRange) => void;
  onPreviousDateRangeChange: (dateRange: DateRange) => void;
  onClearPreviousDateRange: () => void;
}

const AnalyticsBusinessTabHeader: React.FC<AnalyticsBusinessTabHeaderProps> = ({
  publisherId,
  dateRange,
  previousDateRange,
  onAttributeFiltersChange,
  onDateRangeChange,
  onPreviousDateRangeChange,
  onClearPreviousDateRange
}) => {
  const [selectedAttributeNames, setSelectedAttributeNames] = useState<string[]>(
    []
  );
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<
    string[]
  >([]);

  const apiDateRange = useMemo(() => getWholeRange(dateRange), [dateRange]);

  const { data: attributeNamesData, isLoading: isLoadingAttributeNames } =
    useAnalyticsAttributeNames({
      publisherId,
      startDate: apiDateRange!.from!.toISOString(),
      endDate: apiDateRange!.to!.toISOString(),
    });

  const { data: attributeValuesData, isLoading: isLoadingAttributeValues } =
    useAnalyticsAttributeValues({
      publisherId,
      attributeNames: selectedAttributeNames,
      startDate: apiDateRange!.from!.toISOString(),
      endDate: apiDateRange!.to!.toISOString()
    });

  const attributeNameOptions = useMemo(
    () => formatAttributeNameOptions(attributeNamesData),
    [attributeNamesData]
  );

  const { attributeValueOptions, attributeValueMap } = useMemo(
    () =>
      formatAttributeValueOptions(
        selectedAttributeNames,
        attributeValuesData,
        isLoadingAttributeValues
      ),
    [attributeValuesData, selectedAttributeNames, isLoadingAttributeValues]
  );

  const attributeFilters = useMemo(
    () => buildAttributeFilters(selectedAttributeValues, attributeValueMap),
    [selectedAttributeValues, attributeValueMap]
  );

  useEffect(() => {
    onAttributeFiltersChange(attributeFilters);
  }, [attributeFilters, onAttributeFiltersChange]);

  const handleAttributeNameChange = (values: string[]) => {
    setSelectedAttributeNames(values);
    setSelectedAttributeValues([]);
  };

  return (
    <div className="w-full mb-4 flex justify-between items-center pb-2">
      <div className="flex items-center gap-2">
        <FilterCommandCard
          isLabelSearchBy
          header="Attribute Name"
          options={attributeNameOptions}
          filterValue={selectedAttributeNames}
          setFilterValue={handleAttributeNameChange}
          isMulti
          isLoading={isLoadingAttributeNames}
        />
        <FilterCommandCard
          header="Attribute Values"
          options={attributeValueOptions}
          filterValue={selectedAttributeValues}
          setFilterValue={setSelectedAttributeValues}
          isMulti
          disabled={selectedAttributeNames.length === 0}
          isLoading={isLoadingAttributeValues}
        />
      </div>
      <div className="flex items-center w-auto gap-2">
        <DatePicker
          shouldDisplayTime={false}
          mode={DatePickerMode.RANGE}
          value={dateRange}
          onApply={(value) => onDateRangeChange(value as DateRange)}
          shouldExcludeFuture
        />
        <DatePicker
          shouldDisplayTime={false}
          mode={DatePickerMode.RANGE}
          value={previousDateRange}
          onApply={(value) => onPreviousDateRangeChange(value as DateRange)}
          onClear={onClearPreviousDateRange}
          placeholder="Compare Dates"
          disabled={dateRange?.from === undefined || dateRange?.to === undefined}
          shouldExcludeFuture
          disableDatesAfter={dateRange.to}
          fixedRangeDays={getFixedDays(dateRange)}
        />
      </div>
    </div>
  );
};

export default AnalyticsBusinessTabHeader; 